import { useEffect } from 'react';
import { useCalendarInvalidation } from './useCalendarInvalidation';
import { calendarEvents, CalendarEventType } from '@/utils/calendarEvents';

/**
 * Hook that automatically refreshes calendar data when relevant events occur
 * Use this in the main calendar component to ensure it stays up to date
 */
export const useCalendarAutoRefresh = () => {
  const { 
    invalidateCalendar, 
    invalidatePlayer, 
    invalidateTeam, 
    invalidateLeague, 
    invalidateSync, 
    invalidateMatch,
    refetchCalendar 
  } = useCalendarInvalidation();

  useEffect(() => {
    // Map of event types to their corresponding invalidation functions
    const eventHandlers: Record<CalendarEventType, () => void> = {
      'player-added': invalidatePlayer,
      'player-updated': invalidatePlayer,
      'player-deleted': invalidatePlayer,
      'player-assigned': invalidatePlayer,
      'player-unassigned': invalidatePlayer,
      
      'team-created': invalidateTeam,
      'team-updated': invalidateTeam,
      'team-deleted': invalidateTeam,
      
      'league-created': invalidateLeague,
      'league-updated': invalidateLeague,
      'league-deleted': invalidateLeague,
      'league-synced': invalidateLeague,
      
      'schedule-imported': invalidateSync,
      'schedule-synced': invalidateSync,
      
      'match-created': invalidateMatch,
      'match-updated': invalidateMatch,
      'match-deleted': invalidateMatch,
      
      'data-refresh-needed': invalidateCalendar
    };

    // Subscribe to all calendar events
    const unsubscribe = calendarEvents.on('all', (event) => {
      console.log('Calendar auto-refresh triggered by event:', event.type, event.data);
      
      // Call the appropriate invalidation function
      const handler = eventHandlers[event.type];
      if (handler) {
        handler();
      } else {
        // Fallback to general calendar invalidation
        invalidateCalendar();
      }
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, [invalidateCalendar, invalidatePlayer, invalidateTeam, invalidateLeague, invalidateSync, invalidateMatch]);

  // Return function to manually trigger refresh
  return {
    refreshCalendar: refetchCalendar
  };
};
